import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u5_x3_drucksensor';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: '',
    body: 'Differenzdrucksensoren messen den Druckunterschied zwischen zwei Punkten. In der HLK-Technik sind sie unerlässlich für Aufgaben wie Filterüberwachung, Volumenstromregelung und Raumdruckregelung. Der Thermokon DPA2500+ VV Multirange ist ein Beispiel für einen solchen aktiven Sensor.'
  },
  {
    heading: 'Thermokon DPA2500+ VV Multirange im Detail',
    body: `Der DPA2500+ VV ist ein aktiver Differenzdrucksensor mit einem 0-10V DC Spannungsausgang. 'Multirange' bedeutet, dass er über mehrere per DIP-Schalter einstellbare Messbereiche verfügt (z.B. 0-100 Pa, 0-500 Pa, 0-2500 Pa – **genaue Bereiche siehe Datenblatt!**). Er benötigt eine 24V AC/DC Spannungsversorgung.

**Druckanschlüsse:** P1 (+) für den höheren, P2 (-) für den niedrigeren Druck.
**Elektrische Anschlüsse (0-10V):** G (+24V), G0 (Masse), U (0-10V Out), M (Signalmasse).`
  },
  {
    heading: 'Messbereichseinstellung und Skalierung',
    body: `**1. Messbereich am Sensor wählen:** Mit den DIP-Schaltern am DPA2500+ muss der für die Anwendung passende Messbereich eingestellt werden. Dieser Schritt ist entscheidend für die Genauigkeit.

**2. Skalierung in der DDC:** Der am Sensor gewählte Messbereich muss exakt in der DDC-Software für den Analogeingang konfiguriert werden. Beispiel: Ist am Sensor 0-1000 Pa eingestellt, so entspricht 0V = 0 Pa und 10V = 1000 Pa.

**Wichtigkeit:** Eine Abweichung zwischen der Einstellung am Sensor und der Skalierung in der DDC führt zu falschen Messwerten und damit zu Fehlfunktionen der Anlage (z.B. ineffiziente Lüftersteuerung, falsche Filteralarme).`
  },
  {
    heading: 'Nullpunktabgleich (Zeroing)',
    body: 'Um die Messgenauigkeit sicherzustellen, verfügen viele Differenzdrucksensoren über eine Funktion zum Nullpunktabgleich. Dabei werden beide Druckanschlüsse kurz dem gleichen Druck (Atmosphärendruck) ausgesetzt und ein Taster am Gerät betätigt. Dies sollte bei der Inbetriebnahme und periodisch erfolgen.'
  },
  {
    heading: '',
    body: 'Die korrekte Auswahl des Messbereichs, die präzise Skalierung in der DDC und der regelmäßige Nullpunktabgleich sind entscheidend für den zuverlässigen Betrieb von Differenzdrucksensoren wie dem DPA2500+ und somit für die Effizienz und Funktion der gesamten HLK-Anlage.'
  }
];

const images = [
  { id: 'img_dpa2500_beispiel', label: 'Thermokon DPA2500+', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Beispiel eines Thermokon DPA2500+ Drucksensors' },
  { id: 'img_dpa2500_dip', label: 'DIP-Schalter', src: `${IMAGE_BASE}zzz_help.png`, alt: 'DIP-Schalter zur Messbereichseinstellung' },
  { id: 'img_schaltplan_anschluss', label: 'Schaltplan DPA2500+', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Schaltplan: Anschluss DPA2500+ an DDC' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => {
    return mergedBlocks.map(b => `${b.heading ? b.heading + '. ' : ''}${b.body}`).join(' ');
  }, [mergedBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '6.5.3 Differenzdrucksensoren (z.B. DPA2500+)', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      // Bilder-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img =>
          React.createElement(ZoomableImage, {
            key: img.id,
            src: imgSrc(img.id, img.src),
            alt: img.alt,
            label: img.label,
            onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined
          })
        )
      ),

      // Text-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) =>
          React.createElement(EditableTextBlock, {
            key: i,
            pageId: PAGE_ID,
            blockId: `block_${i}`,
            heading: block.heading,
            body: block.body,
            onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody)
          })
        ),

        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, {
            variant: 'outlined',
            onClick: () => navigate('/units/m6_u5_x99_quiz')
          }, 'Zum Quiz')
        )
      )
    )
  );
};

export default Page;


